import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

interface SelectOption {
    value: string;
    label: string;
}

interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: string[] | SelectOption[];
    placeholder?: string;
    label?: string;
    className?: string;
    disabled?: boolean;
    searchable?: boolean;
    dropdownClassName?: string;
    size?: 'sm' | 'md' | 'admin';
    fullWidth?: boolean;
    prefixIcon?: React.ReactNode;
    dropdownPosition?: 'vertical' | 'horizontal';
}

const Select = ({
    value,
    onChange,
    options,
    placeholder = 'Select option',
    className = '',
    disabled = false,
    searchable = false,
    dropdownClassName = '',
    size = 'md',
    fullWidth = true,
    prefixIcon,
    dropdownPosition = 'vertical'
}: SelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [position, setPosition] = useState<{ top: number | 'auto'; left: number; width: number; bottom: number | 'auto' }>({ top: 0, left: 0, width: 0, bottom: 'auto' });
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Normalize options
    const normalizedOptions: SelectOption[] = options.map(opt =>
        typeof opt === 'string' ? { value: opt, label: opt } : opt
    );

    const filteredOptions = normalizedOptions.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = normalizedOptions.find(opt => opt.value === value);

    useEffect(() => {
        if (isOpen && searchable) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 50);
        }
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen, searchable]);

    const updatePosition = () => {
        if (containerRef.current && isOpen) {
            const rect = containerRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const padding = 16;
            const dropdownWidth = 170; // estimated dropdown width for horizontal
            const dropdownHeight = 240; // max-h-60 estimate

            const spaceRight = viewportWidth - rect.right;
            const spaceLeft = rect.left;
            const canShowRight = spaceRight >= dropdownWidth + padding;
            const canShowLeft = spaceLeft >= dropdownWidth + padding;

            // Use horizontal if requested AND there's space on either side
            const useHorizontal = dropdownPosition === 'horizontal' && (canShowRight || canShowLeft);

            if (useHorizontal) {
                // Horizontal positioning (left/right of button)
                const showOnLeft = !canShowRight && canShowLeft;

                // Align with top of button, but stay within viewport
                let topPosition = rect.top;
                if (topPosition + dropdownHeight > viewportHeight - padding) {
                    topPosition = Math.max(padding, viewportHeight - dropdownHeight - padding);
                }

                setPosition({
                    top: topPosition,
                    bottom: 'auto',
                    left: showOnLeft ? rect.left - dropdownWidth - 8 : rect.right + 8,
                    width: dropdownWidth
                });
            } else {
                // Vertical positioning (above/below button) - default fallback
                const spaceBelow = viewportHeight - rect.bottom;
                const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

                // For horizontal fallback, use a wider dropdown
                const targetWidth = dropdownPosition === 'horizontal' ? Math.max(rect.width, dropdownWidth) : rect.width;
                const maxWidth = viewportWidth - padding * 2;
                const calculatedWidth = fullWidth ? Math.min(targetWidth, maxWidth) : targetWidth;

                // Center the dropdown if it's wider than the button
                let leftPosition = rect.left;
                if (calculatedWidth > rect.width) {
                    leftPosition = rect.left - (calculatedWidth - rect.width) / 2;
                }
                // Keep within viewport bounds
                if (leftPosition < padding) {
                    leftPosition = padding;
                } else if (leftPosition + calculatedWidth > viewportWidth - padding) {
                    leftPosition = viewportWidth - calculatedWidth - padding;
                }

                setPosition({
                    top: showAbove ? 'auto' : rect.bottom + 4,
                    bottom: showAbove ? viewportHeight - rect.top + 4 : 'auto',
                    left: leftPosition,
                    width: calculatedWidth
                });
            }
        }
    };

    useEffect(() => {
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        if (isOpen) updatePosition();

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // Check if click is outside both the container and the dropdown portal
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div ref={containerRef} className={`relative ${fullWidth ? 'w-full' : 'w-fit'} ${className}`}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`${fullWidth ? 'w-full justify-between' : 'w-fit justify-start'} flex items-center text-left bg-white/5 border border-white/20 transition-all font-sans ${size === 'sm' ? 'px-3 py-1.5 text-xs md:text-sm gap-2 rounded-xl' :
                    size === 'admin' ? 'px-3 h-9 text-[11px] gap-2 rounded-xl' :
                        'px-4 py-3 md:py-4 text-sm md:text-base gap-3 rounded-2xl'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent'
                    } ${isOpen ? 'ring-2 ring-primary-500/50 border-transparent bg-white/10' : ''}`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {prefixIcon && <div className="shrink-0 text-primary-400">{prefixIcon}</div>}
                    <span className={`truncate ${!selectedOption ? 'text-white/30' : 'text-white'}`}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                </div>
                <ChevronDown
                    size={16}
                    className={`text-white/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className={`fixed z-[9999] bg-cosmic-900/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden max-h-60 overflow-y-auto ${dropdownClassName}`}
                    style={{
                        top: position.top,
                        bottom: position.bottom,
                        left: position.left,
                        width: dropdownPosition === 'horizontal' ? position.width : (fullWidth ? position.width : 'auto'),
                        minWidth: dropdownPosition === 'horizontal' ? position.width : (fullWidth ? '0' : Math.max(position.width, 120)),
                    }}
                >
                    {searchable && (
                        <div className="sticky top-0 p-2 bg-cosmic-900/80 backdrop-blur-xl border-b border-white/10 z-10">
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full bg-white/5 border border-white/20 rounded-xl pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent transition-all placeholder-white/30"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <button
                                key={`${option.value}-${index}`}
                                type="button"
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full px-4 py-2.5 text-left flex items-center justify-between transition-all border-b border-white/5 last:border-b-0 ${option.value === value
                                    ? 'bg-primary-600/20 text-white font-medium'
                                    : 'text-white/80 hover:bg-white/10'
                                    }`}
                            >
                                <span className="truncate">{option.label}</span>
                                {option.value === value && (
                                    <Check size={14} className="text-primary-400 shrink-0 ml-2" />
                                )}
                            </button>
                        ))) : (
                        <div className="px-4 py-8 text-center text-white/50 text-sm">
                            No options found
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default Select;
